import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { client } from '../axios'
import { BillingIntervalType, SubscriptionPlanType, SubscriptionStatus } from '../../../types/subscription'
import { tenantRouterKeys } from 'src/api/routes/tenant'

export enum SubscriptionTags {
  Get = 'SubscriptionGet',
  GetPlans = 'SubscriptionGetPlans',
  ChangePlan = 'SubscriptionChangePlan',
  Cancel = 'SubscriptionCancel',
}

export interface SubscriptionResponse {
  currentPlan: SubscriptionPlanType
  status: SubscriptionStatus
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  interval: BillingIntervalType | null
}

export interface ISubscriptionPlan {
  plan: SubscriptionPlanType
  name: string
  description: string
  priceInCents: number
  interval: BillingIntervalType
}

export interface SubscriptionPlansResponse {
  plans: ISubscriptionPlan[]
}

export interface GetPlanParams {
  interval: BillingIntervalType
}

export interface ChangePlanRequest extends GetPlanParams {
  plan: SubscriptionPlanType
}

export function useGetSubscription() {
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useQuery({
    queryKey: [SubscriptionTags.Get, selectedTenantId],
    queryFn: async () => {
      const response = await client.get<SubscriptionResponse>(`/tenants/${selectedTenantId}/subscriptions`)
      return response.data
    },
    enabled: Boolean(selectedTenantId),
  })
}

export function useGetSubscriptionPlans(params: GetPlanParams) {
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useQuery({
    queryKey: [SubscriptionTags.GetPlans, selectedTenantId, params],
    queryFn: async () => {
      const response = await client.get<SubscriptionPlansResponse>(`/tenants/${selectedTenantId}/subscriptions/plans`, {
        params,
      })
      return response.data
    },
    enabled: Boolean(selectedTenantId),
  })
}

export function useChangePlan() {
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [SubscriptionTags.ChangePlan],
    mutationFn: async ({ plan, interval }: ChangePlanRequest) => {
      const response = await client.post<SubscriptionResponse>(
        `/tenants/${selectedTenantId}/subscriptions/change-plan`,
        { plan, interval },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SubscriptionTags.Get, selectedTenantId],
      })
      queryClient.invalidateQueries({
        queryKey: [tenantRouterKeys.myTenants],
      })
    },
  })
}

export function useCancelSubscription() {
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [SubscriptionTags.Cancel],
    mutationFn: async () => {
      const response = await client.post<SubscriptionResponse>(`/tenants/${selectedTenantId}/subscriptions/cancel`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SubscriptionTags.Get, selectedTenantId],
      })
    },
  })
}
